/**
 * Outbound resource — the agent dialer's "Message" tab (WhatsApp/SMS to any number) and the
 * availability probe that gates the dialer UI (ARCHITECTURE §H).
 */

import type { HttpClient } from '../httpClient';
import { endpoints } from '../endpoints';

/** An approved WhatsApp template the org has registered (for cold-contact first sends). */
export interface WaTemplate {
  name: string;
  language: string;
  body: string;
}

/** The org's manual template list + the WABA id used to fetch the live Meta list. */
export interface WaTemplatesConfig {
  templates: WaTemplate[];
  /** WhatsApp Business Account id (needed to list templates from Meta). */
  wabaId: string;
}

/** A template as registered on the org's Meta WhatsApp Business Account (live fetch). */
export interface MetaWaTemplate {
  name: string;
  language: string;
  /** APPROVED | PENDING | REJECTED | … */
  status: string;
  category: string;
  body: string;
}

/** What the dialer can offer this org right now. */
export interface OutboundAvailability {
  /** Softphone configured → the Call tab can place PSTN calls. */
  call: boolean;
  /** A connected + enabled WhatsApp channel → can message. */
  whatsapp: boolean;
  /** A connected + enabled SMS channel → can message. */
  sms: boolean;
  /** Approved WhatsApp templates (only relevant for cold WhatsApp contacts). */
  waTemplates: WaTemplate[];
  /** The org's default country calling code (e.g. "1"), derived from its own Twilio number. Used to
   *  complete a bare national number to E.164 before dialing (Twilio rejects non-E.164 → err 13224). */
  defaultCallingCode?: string;
}

export interface OutboundMessageInput {
  to: string;
  channel: 'whatsapp' | 'sms';
  body?: string;
  /** Approved WhatsApp template name (cold contact / outside the 24h window). */
  templateName?: string;
  templateLanguage?: string;
  /** Ordered values for the template's body variables ({{1}}, {{2}}, …) — the dynamic data
   *  (name, order id, …). Omit / empty for a template with no variables. */
  templateParams?: string[];
}

export class OutboundResource {
  constructor(private readonly http: HttpClient) {}

  /** Which dialer capabilities this org has (call / whatsapp / sms) + approved WA templates. */
  availability(): Promise<OutboundAvailability> {
    return this.http.get(endpoints.outbound.availability());
  }

  /** Send a WhatsApp/SMS to a dialed number; resolves to the conversation it landed in.
   *  `delivered` is false when the provider rejected the send (e.g. a cold WhatsApp contact). */
  sendMessage(
    input: OutboundMessageInput,
  ): Promise<{ sessionId: string; delivered: boolean; deliveryHint?: string | null }> {
    return this.http.post(endpoints.outbound.message(), input);
  }

  /** The org's manual approved-template list + WABA id (admin — Settings → WhatsApp templates). */
  waTemplates(): Promise<WaTemplatesConfig> {
    return this.http.get(endpoints.outbound.waTemplates());
  }

  /** Replace the org's approved templates + WABA id (admin). */
  setWaTemplates(templates: WaTemplate[], wabaId: string): Promise<WaTemplatesConfig> {
    return this.http.put(endpoints.outbound.waTemplates(), { templates, wabaId });
  }

  /** Live-fetch the templates registered on the org's Meta WhatsApp account (admin). */
  metaWaTemplates(): Promise<MetaWaTemplate[]> {
    return this.http.get(endpoints.outbound.metaWaTemplates());
  }
}
